<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { trackReportFeature } from "@/analytics/report-analytics";
import { useTranslation } from "i18next-vue";
import { useZIndex } from "element-plus";
import { useDates } from "@/composable/useDates";
import type { FieldObservation } from "@/types/field-observations.types";
import { getObservationTypeConfig } from "@/components/field-observations/observation-type";
import APIClient from "@/APIClient";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";

const props = defineProps<{
  modelValue: boolean;
  /** Observations sources — une constatation peut regrouper N désordres. */
  observations: FieldObservation[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { t } = useTranslation();
const { formatDistanceToNow } = useDates();
const { nextZIndex } = useZIndex();

const drawerZIndex = ref(3000);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

// Navigation entre les observations sources (‹ 1/3 ›)
const currentIndex = ref(0);
const observation = computed(
  () => props.observations[currentIndex.value] ?? props.observations[0],
);
const goTo = (delta: number) => {
  const len = props.observations.length;
  currentIndex.value = (currentIndex.value + delta + len) % len;
};

// Ensure drawer z-index is always above any parent dialog overlay
const ensureTopZIndex = () => {
  // Call nextZIndex twice to skip past the dialog's overlay z-index
  nextZIndex();
  drawerZIndex.value = nextZIndex();
};

// Type config (codes visuels partagés avec l'outil terrain et le canvas)
const typeConfig = computed(() => {
  const config = getObservationTypeConfig(observation.value.type);
  return {
    icon: config.icon,
    color: config.color,
    label: config.labelKey ? t(config.labelKey) : "",
  };
});

// Photo URLs
const photoUrls = ref<Map<number, string>>(new Map());
const loadingPhotos = ref<Set<number>>(new Set());

const loadPhotoUrl = async (fileId: number) => {
  if (photoUrls.value.has(fileId) || loadingPhotos.value.has(fileId)) return;
  loadingPhotos.value.add(fileId);
  try {
    const result = await APIClient.getFileUrl(fileId);
    photoUrls.value.set(fileId, result.url);
  } catch (error) {
    console.error("Failed to load photo:", error);
  } finally {
    loadingPhotos.value.delete(fileId);
  }
};

// Audio URLs
const audioUrls = ref<Map<number, string>>(new Map());
const loadingAudios = ref<Set<number>>(new Set());

const loadAudioUrl = async (fileId: number) => {
  if (audioUrls.value.has(fileId) || loadingAudios.value.has(fileId)) return;
  loadingAudios.value.add(fileId);
  try {
    const result = await APIClient.getFileUrl(fileId);
    audioUrls.value.set(fileId, result.url);
  } catch (error) {
    console.error("Failed to load audio:", error);
  } finally {
    loadingAudios.value.delete(fileId);
  }
};

const loadCurrentMedia = () => {
  observation.value?.photos?.forEach((p) => loadPhotoUrl(p.fileId));
  observation.value?.audioRecordings?.forEach((a) => loadAudioUrl(a.fileId));
};

// Load media and grab z-index when drawer opens
watch(isOpen, (open) => {
  if (open) {
    trackReportFeature("source_observation_viewed");
    currentIndex.value = 0;
    ensureTopZIndex();
    loadCurrentMedia();
  }
});

// Les URLs de l'observation affichée (navigation entre sources)
watch(currentIndex, loadCurrentMedia);

const photos = computed(() => observation.value?.photos ?? []);
const audioRecordings = computed(
  () => observation.value?.audioRecordings ?? [],
);

const authorName = computed(() => observation.value?.author?.full_name ?? "");
const authorInitials = computed(() => {
  const name = authorName.value;
  const parts = name.split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
});

// Photo preview
const previewUrl = ref<string | null>(null);
</script>

<template>
  <el-drawer
    v-model="isOpen"
    size="460px"
    :close-on-click-modal="true"
    append-to-body
    :z-index="drawerZIndex"
  >
    <template #header>
      <div class="drawer-header">
        <div class="drawer-title">
          {{ t("report:findings.sourceObservation.drawerTitle") }}
        </div>
        <div class="drawer-subtitle">
          {{ t("report:findings.sourceObservation.drawerDescription") }}
        </div>
      </div>
    </template>

    <!-- Navigation entre les observations sources regroupées -->
    <div
      v-if="observations.length > 1"
      class="source-nav d-flex align-items-center justify-content-center gap-2"
    >
      <DRBtnIcon
        variant="flat"
        :size="26"
        phosphor-icon="caret-left"
        @click="goTo(-1)"
      />
      <span class="source-nav-label">
        {{
          t("report:findings.sourceObservation.counter", {
            index: currentIndex + 1,
            total: observations.length,
          })
        }}
      </span>
      <DRBtnIcon
        variant="flat"
        :size="26"
        phosphor-icon="caret-right"
        @click="goTo(1)"
      />
    </div>

    <div class="drawer-body">
      <!-- Author + date (top) -->
      <div v-if="authorName" class="obs-author">
        <span class="author-avatar">{{ authorInitials }}</span>
        <span class="author-text">
          {{
            t("report:findings.sourceObservation.addedBy", {
              name: authorName,
              time: formatDistanceToNow(observation.createdAt),
            })
          }}
        </span>
      </div>

      <!-- Type + part badges -->
      <div class="obs-header">
        <div
          class="type-badge"
          :style="{
            backgroundColor: typeConfig.color + '15',
            color: typeConfig.color,
          }"
        >
          <PhosphorIcon :name="typeConfig.icon" :size="16" weight="bold" />
          {{ typeConfig.label }}
        </div>
        <span v-if="observation.part" class="part-badge">
          {{ observation.part.name }}
        </span>
      </div>

      <!-- Notes -->
      <section class="obs-section">
        <h4 class="section-title">
          <PhosphorIcon name="note" :size="18" />
          {{ t("report:findings.sourceObservation.notes") }}
        </h4>
        <p v-if="observation.description" class="obs-description">
          {{ observation.description }}
        </p>
        <p v-else class="obs-empty">
          {{ t("report:findings.sourceObservation.noDescription") }}
        </p>
      </section>

      <!-- Voice notes -->
      <section class="obs-section">
        <h4 class="section-title">
          <PhosphorIcon name="microphone" :size="18" />
          {{ t("report:findings.sourceObservation.voiceNotes") }}
          <span v-if="audioRecordings.length" class="count-badge">{{
            audioRecordings.length
          }}</span>
        </h4>
        <div v-if="audioRecordings.length" class="audio-list">
          <div
            v-for="audio in audioRecordings"
            :key="audio.id"
            class="audio-item"
          >
            <div v-if="loadingAudios.has(audio.fileId)" class="audio-loading">
              <span class="spinner-border spinner-border-sm" />
            </div>
            <audio
              v-else-if="audioUrls.get(audio.fileId)"
              :src="audioUrls.get(audio.fileId)"
              class="audio-player"
              controls
            />
            <p v-if="audio.transcription" class="audio-transcription">
              <span class="transcription-label">{{
                t("report:findings.sourceObservation.transcription")
              }}</span>
              {{ audio.transcription }}
            </p>
          </div>
        </div>
        <p v-else class="obs-empty">
          {{ t("report:findings.sourceObservation.noVoiceNotes") }}
        </p>
      </section>

      <!-- Photos -->
      <section class="obs-section">
        <h4 class="section-title">
          <PhosphorIcon name="camera" :size="18" />
          {{ t("report:findings.sourceObservation.photos") }}
          <span v-if="photos.length" class="count-badge">{{
            photos.length
          }}</span>
        </h4>
        <div v-if="photos.length" class="photo-grid">
          <div
            v-for="photo in photos"
            :key="photo.id"
            class="photo-item"
            @click="previewUrl = photoUrls.get(photo.fileId) ?? null"
          >
            <img
              v-if="photoUrls.get(photo.fileId)"
              :src="photoUrls.get(photo.fileId)"
              alt=""
            />
            <div v-else class="photo-loading">
              <span class="spinner-border spinner-border-sm" />
            </div>
          </div>
        </div>
        <p v-else class="obs-empty">
          {{ t("report:findings.sourceObservation.noPhotos") }}
        </p>
      </section>
    </div>
  </el-drawer>

  <!-- Full-screen photo preview -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="previewUrl"
        class="photo-preview-overlay"
        :style="{ zIndex: drawerZIndex + 1 }"
        @click="previewUrl = null"
      >
        <img :src="previewUrl" class="photo-preview-img" />
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
:deep(.el-drawer__header) {
  border-bottom: 1px solid var(--bs-border-color);
  margin-bottom: 0;
  padding-bottom: 12px;
}

:deep(.el-drawer__body) {
  padding: 0;
}

.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bs-body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-subtitle {
  font-size: 12px;
  color: var(--bs-tertiary-color);
}

.source-nav {
  padding: 10px 14px 0;
}

.source-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--bs-secondary-color);
  min-width: 110px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.drawer-body {
  margin: 14px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: white;
}

.obs-author {
  display: flex;
  align-items: center;
  gap: 8px;
}

.author-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.author-text {
  font-size: 13px;
  color: var(--bs-secondary-color);
}

.obs-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.part-badge {
  font-size: 12px;
  padding: 3px 8px;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
  border-radius: var(--bs-border-radius);
}

.obs-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
}

.count-badge {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.obs-description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--bs-body-color);
  margin: 0;
  white-space: pre-wrap;
}

.obs-empty {
  font-size: 13px;
  color: var(--bs-tertiary-color);
  font-style: italic;
  margin: 0;
}

.audio-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audio-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.audio-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  color: var(--bs-secondary-color);
}

.audio-player {
  width: 100%;
  height: 36px;
}

.audio-transcription {
  font-size: 13px;
  color: var(--bs-secondary-color);
  margin: 0;
  line-height: 1.5;
}

.transcription-label {
  font-weight: 600;
  color: var(--bs-tertiary-color);
  margin-right: 4px;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.photo-item {
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--bs-secondary-bg);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.photo-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-secondary-color);
}

// Full-screen photo preview
.photo-preview-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.photo-preview-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
